import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { FormTransformers } from '@/common/utils/formTransformers'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { rejectRewardTicketSchema, RejectRewardTicketValidationSchema } from '../schemas/rejectRewardTicket.schema'
import { useRejectRewardTicket } from '../hooks/useRejectRewardTicket'
import { useQueryClient } from '@tanstack/react-query'
import TextArea from '@/common/components/TextArea'

interface RejectRewardTicketModalProps {
  raffleId?: string
  rewardTicketId?: string
  setIsError: Dispatch<SetStateAction<boolean>>
  setMessages: Dispatch<SetStateAction<boolean | string[]>>
}

export const RejectRewardTicketModal = ({ 
  raffleId, 
  rewardTicketId,
  setIsError,
  setMessages,
 }: RejectRewardTicketModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)

  const { mutate: rejectRewardTicket, isPending: rejectRewardTicketIsPending } = useRejectRewardTicket({
    id: rewardTicketId,
    onSuccess: () => {
      setIsError(false)
      queryClient.invalidateQueries({ queryKey: ['tmaisRaffle', raffleId] })
      queryClient.invalidateQueries({ queryKey: ['rewardTickets'] })
      setMessages(['Ticket rejeitado com sucesso!'])
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setIsError(true)
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    },
  })

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<RejectRewardTicketValidationSchema>({
    resolver: zodResolver(rejectRewardTicketSchema),
    defaultValues: {
      reject_reason: '',
    },
  })

  const onSubmit: SubmitHandler<RejectRewardTicketValidationSchema> = (data) => {
    rejectRewardTicket(data)
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value)
          reset()
        }}
        Trigger={
          <Button 
            className='bg-danger-secondary hover:bg-danger-secondary/80'
            operationKlassToCheckPermission='Establishment::Tmais::RewardTickets::Reject'
          >
            Desclassificar
          </Button>
        }
        title="Rejeitar ticket"
        useCancellableFooter
        confirmButtonText="Confirmar rejeição"
        onConfirm={handleSubmit(onSubmit)}
        disabled={rejectRewardTicketIsPending}
        confirmButtonIsLoading={rejectRewardTicketIsPending}
        body={
          <form className="flex flex-col gap-4">
            <TextArea
              label="Motivo da rejeição do ticket"
              markAsRequired
              maxLength={64}
              {...register('reject_reason')}
              errorMessage={errors.reject_reason?.message || undefined}
              variant={errors.reject_reason?.message ? 'error' : 'default'}
            />
          </form>
        }
      />
    </>
  )
}
